import { usePageLayoutPortalContext } from './PageLayoutPortalContext.js';
import type { SlotRegistryState } from './slot-registry.js';
import { useSafeLayoutEffect } from '../../../core/hooks/useSafeLayoutEffect.js';

/**
 * Registers `config` for `slotName` in the slot registry on mount and
 * unregisters on unmount. Re-registers when `config` identity changes.
 *
 * Uses a layout effect so the registration is observable before the next
 * paint — the layout shells (`MainShell`, `HeaderShell`) need to see the
 * registered config in the same commit as the slot component mounts to
 * avoid a frame of unstyled content.
 *
 * Callers must memoize `config` themselves (typically via `useMemo` with an
 * explicit dependency array) so the effect only re-runs when something
 * meaningful changes.
 *
 * @internal
 */
export function useRegisteredSlot<K extends keyof SlotRegistryState>(
  slotName: K,
  config: NonNullable<SlotRegistryState[K]>,
): void {
  const { registry } = usePageLayoutPortalContext();
  useSafeLayoutEffect(() => {
    registry.register(slotName, config);
    return () => registry.unregister(slotName);
  }, [registry, slotName, config]);
}
